import { BillingInterval, BillingIntervalToggle, billingPrice } from "./BillingInterval";
import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Divider,
  Link,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Type,
  Tier,
  Subscription,
  StripeSubscriptionDetail,
  isTrialing,
} from "./types";
import {
  PRICING,
  SPORT_CATEGORIES,
  TIER_INFO,
  DOC_URLS,
} from "./pricingData";
import { AsyncButton } from "./AsyncButton";
import {
  isAllAccessType,
  ALL_ACCESS_PRICE_LABEL_MAP,
  ALL_ACCESS_DISPLAY_PRICE,
} from "./allAccess";
import {
  getStoryStatsProductInfo,
  getStoryStatsPriceLabel,
  isStoryStatsType,
} from "./storyStatsPricing";
import { ReactComponent as Baseball } from "./assets/baseball.svg";
import { ReactComponent as Soccer } from "./assets/soccer.svg";
import { ReactComponent as Football } from "./assets/football.svg";
import { ReactComponent as Basketball } from "./assets/basketball.svg";
import { ReactComponent as AllBalls } from "./assets/allballs.svg";
import { ReactComponent as Hockey } from "./assets/hockey.svg";
import { ReactComponent as Glove } from "./assets/glove.svg";
import { ReactComponent as Gaming } from "./assets/gaming.svg";
import { ReactComponent as Golf } from "./assets/golf.svg";
import { ReactComponent as Tennis } from "./assets/tennis.svg";
import { ReactComponent as F1 } from "./assets/f1.svg";

// Format "ends in 41h 12m" from an ISO trialEnd. Returns null if trialEnd is null or past.
const formatTrialRemaining = (trialEnd: string | null): string | null => {
  if (!trialEnd) return null;
  const diffMs = new Date(trialEnd).getTime() - Date.now();
  if (diffMs <= 0) return null;
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

const formatAbsoluteDateTime = (iso: string | null): string | null => {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// Sport icon mapping
const getSportIcon = (type: Type) => {
  if (SPORT_CATEGORIES.basketball.includes(type)) return <Basketball />;
  const iconMap: Record<string, React.ReactNode> = {
    nba: <Basketball />,
    stories_nba: <Basketball />,
    stories_wnba: <Basketball />,
    nfl: <Football />,
    mlb: <Baseball />,
    stories_mlb: <Baseball />,
    epl: <Soccer />,
    nhl: <Hockey />,
    stories_nhl: <Hockey />,
    stories_nfl: <Football />,
    stories_soccer: <Soccer />,
    wnba: <Basketball />,
    ncaaf: <Football />,
    ncaab: <Basketball />,
    ncaaw: <Basketball />,
    mma: <Glove />,
    fifa: <Soccer />,
    laliga: <Soccer />,
    seriea: <Soccer />,
    ucl: <Soccer />,
    bundesliga: <Soccer />,
    ligue1: <Soccer />,
    mls: <Soccer />,
    cs: <Gaming />,
    valorant: <Gaming />,
    lol: <Gaming />,
    dota2: <Gaming />,
    pga: <Golf />,
    atp: <Tennis />,
    wta: <Tennis />,
    f1: <F1 />,
    cbb: <Baseball />,
  };
  return iconMap[type] || <AllBalls />;
};

// Tier Comparison Table - shows what each tier includes
export const TierComparisonTable = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const features = [
    { name: "Teams & Players", free: true, allstar: true, goat: true },
    { name: "Game Schedules", free: true, allstar: true, goat: true },
    { name: "Basic Stats", free: false, allstar: true, goat: true },
    { name: "Standings & Rankings", free: false, allstar: true, goat: true },
    { name: "Injuries", free: false, allstar: true, goat: true },
    { name: "Season Stats", free: false, allstar: false, goat: true },
    { name: "Advanced Analytics", free: false, allstar: false, goat: true },
    { name: "Live Data & Box Scores", free: false, allstar: false, goat: true },
    { name: "Betting Odds", free: false, allstar: false, goat: true },
    { name: "Player Props", free: false, allstar: false, goat: true },
  ];

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      boxShadow="sm"
    >
      <Table size="sm">
        <Thead bg={headerBg}>
          <Tr>
            <Th py={4} fontSize="xs" textTransform="uppercase" letterSpacing="wide">
              Features
            </Th>
            <Th textAlign="center" py={4}>
              <VStack spacing={0}>
                <Text fontSize="sm" fontWeight="bold" color="gray.500">
                  FREE
                </Text>
                <Text fontSize="xs" color="gray.400">
                  $0
                </Text>
              </VStack>
            </Th>
            <Th textAlign="center" py={4}>
              <VStack spacing={0}>
                <Text fontSize="sm" fontWeight="bold" color="blue.500">
                  ALL-STAR
                </Text>
                <Text fontSize="xs" color="gray.400">
                  $9.99/mo
                </Text>
              </VStack>
            </Th>
            <Th textAlign="center" py={4}>
              <VStack spacing={0}>
                <Text fontSize="sm" fontWeight="bold" color="accent">
                  GOAT
                </Text>
                <Text fontSize="xs" color="gray.400">
                  $39.99/mo
                </Text>
              </VStack>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {features.map((feature, index) => (
            <Tr key={feature.name}>
              <Td py={3} fontSize="sm">
                {feature.name}
              </Td>
              <Td textAlign="center">
                {feature.free ? (
                  <CheckIcon color="green.500" boxSize={3} />
                ) : (
                  <CloseIcon color="gray.300" boxSize={2} />
                )}
              </Td>
              <Td textAlign="center">
                {feature.allstar ? (
                  <CheckIcon color="green.500" boxSize={3} />
                ) : (
                  <CloseIcon color="gray.300" boxSize={2} />
                )}
              </Td>
              <Td textAlign="center">
                {feature.goat ? (
                  <CheckIcon color="green.500" boxSize={3} />
                ) : (
                  <CloseIcon color="gray.300" boxSize={2} />
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

// All Access Card - prominent upsell
interface AllAccessBannerProps {
  onBillingIntervalChange?: (value: BillingInterval) => void;
  onSwitchToAnnual?: () => void;
  onUndoPlanChange?: () => void;
  billingInterval?: BillingInterval;
  isSubscribed: boolean;
  subscriptionType?: Type;
  subscriptionDetails?: StripeSubscriptionDetail[];
  onSubscribe: () => void;
  onCancel: () => void;
  onReactivate?: () => void;
  // Trial integration. Optional so existing callers continue to compile.
  isTrialEligible?: boolean;
  onStartTrial?: () => void;
  // When the user is currently trialing ALL-ACCESS, render trial UI in the
  // subscribed branch. Expects the matching subscription row + detail.
  trialSubscription?: Subscription;
  onTrialCancel?: () => void;
  onTrialReactivate?: () => void;
  onTrialConvert?: () => void;
  isBillingLocked?: boolean;
  isTrialConversionPending?: boolean;
}

export const AllAccessBanner: React.FC<AllAccessBannerProps> = ({
  isSubscribed,
  onBillingIntervalChange, onSwitchToAnnual, onUndoPlanChange,
  billingInterval = "month",
  subscriptionType,
  subscriptionDetails,
  onSubscribe,
  onCancel,
  onReactivate,
  isTrialEligible,
  onStartTrial,
  trialSubscription,
  onTrialCancel,
  onTrialReactivate,
  onTrialConvert,
  isBillingLocked,
  isTrialConversionPending,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const dividerColor = useColorModeValue("gray.200", "gray.600");

  if (isSubscribed) {
    const allAccessDetail = subscriptionDetails?.find(
      (d) => isAllAccessType(d.type)
    );
    const isPendingCancel = allAccessDetail?.cancelAtPeriodEnd;
    const trialing = !!trialSubscription && isTrialing(trialSubscription);
    const trialEnd = trialSubscription?.trialEnd ?? null;
    const endDate = allAccessDetail?.currentPeriodEnd
      ? new Date(allAccessDetail.currentPeriodEnd).toLocaleDateString(
          "en-US",
          { year: "numeric", month: "long", day: "numeric" }
        )
      : null;

    const priceLabel = allAccessDetail?.billingInterval === "year" ? billingPrice("all_access_v3", "paid+", "year") : ALL_ACCESS_PRICE_LABEL_MAP[subscriptionType ?? ""] ?? "$299.99/month";

    return (
      <Box
        bg={cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={cardBorder}
        overflow="hidden"
      >
        <Box p={6}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "start", md: "center" }}
            gap={4}
          >
            <VStack align="start" spacing={1}>
              <Flex gap={3} align="center" flexWrap="wrap">
                <Heading size="md" whiteSpace="nowrap">ALL-ACCESS</Heading>
                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                  {priceLabel}
                </Text>
                <Badge
                  colorScheme={
                    trialing ? "red" : isPendingCancel ? "orange" : "green"
                  }
                  whiteSpace="normal"
                  fontSize="xs"
                  variant="subtle"
                >
                  {trialing
                    ? isPendingCancel
                      ? `Trial ends ${formatAbsoluteDateTime(trialEnd)}`
                      : `Trial ends in ${formatTrialRemaining(trialEnd)}`
                    : isPendingCancel
                      ? `Active until ${endDate}`
                      : "ACTIVE"}
                </Badge>
              </Flex>
              <Text fontSize="sm" color="gray.500">
                {trialing ? `Trial ends ${formatAbsoluteDateTime(trialEnd)}` : endDate ? `${isPendingCancel ? "Ends" : "Renews"} ${endDate}` : null}
              </Text>
            </VStack>
            {trialSubscription?.trialPendingAction && trialSubscription.trialEnd ? (
              <PendingTrialAction action={trialSubscription.trialPendingAction}
                onCancel={() => onTrialCancel?.()} onUndo={() => onTrialReactivate?.()} onConvert={() => onTrialConvert?.()} />
            ) : isTrialConversionPending || (trialing && isBillingLocked) ? (
              <Button size="sm" variant="outline" isDisabled>
                Processing
              </Button>
            ) : trialing ? (
              isPendingCancel ? (
                <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                  <AsyncButton size="sm" colorScheme="brand" onClick={() => onTrialReactivate?.()}>Undo cancellation</AsyncButton>
                  <AsyncButton size="sm" variant="outline" onClick={() => onTrialCancel?.()}>End now</AsyncButton>

                </HStack>
              ) : (
                <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                  {onTrialConvert && (
                    <AsyncButton
                      size="sm"
                      colorScheme="brand"
                      onClick={onTrialConvert}
                    >
                      Upgrade now
                    </AsyncButton>
                  )}
                  <AsyncButton
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    onClick={onTrialCancel}
                  >
                    Cancel trial
                  </AsyncButton>
                </HStack>
              )
            ) : isPendingCancel ? (
              <AsyncButton
                size="sm"
                variant="solid"
                colorScheme="brand"
                isDisabled={isBillingLocked}
                onClick={onReactivate}
              >
                Reactivate
              </AsyncButton>
            ) : (
              <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                {!allAccessDetail?.scheduledChange && subscriptionType === "all_access_v3" && allAccessDetail?.billingInterval === "month" && onSwitchToAnnual ? <Button size="sm" isDisabled={isBillingLocked} onClick={onSwitchToAnnual}>Switch to annual</Button> : null}
                <Button size="sm" variant="outline" colorScheme="red" isDisabled={isBillingLocked} onClick={onCancel}>Cancel Plan</Button>
              </HStack>
            )}
          </Flex>
        </Box>
        {allAccessDetail?.scheduledChange && (
          <Flex borderTopWidth="1px" borderColor={dividerColor} px={6} py={4} gap={3} flexWrap="wrap" align="center" justify="space-between">
            <Text fontSize="sm">Plan changes to annual on {new Date(allAccessDetail.scheduledChange.effectiveAt).toLocaleDateString("en-US", {year: "numeric", month: "long", day: "numeric"})}.</Text>
            <AsyncButton size="sm" ml="auto" isDisabled={isBillingLocked} onClick={onUndoPlanChange}>Undo plan change</AsyncButton>
          </Flex>
        )}
        <Box borderTopWidth="1px" borderColor={dividerColor} px={6} py={4}>
          <Text fontSize="sm" fontWeight="500">
            All sports, all endpoints, BDL Lab, Webhooks, and StoryStats access
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={cardBorder}
      p={6}
    >
      {onBillingIntervalChange && (
        <Flex mb={4} justify="flex-start">
          <BillingIntervalToggle value={billingInterval} onChange={onBillingIntervalChange} disabled={isBillingLocked} />
        </Flex>
      )}
      <Flex
        direction={{ base: "column", md: "row" }}
        align="start"
        justify="space-between"
        gap={4}
      >
        <VStack align="start" spacing={1}>
          <Heading size="md">Upgrade to ALL-ACCESS</Heading>
          <Text fontSize="sm" color="gray.500">
            Every sport. Every endpoint. One subscription. Includes BDL Lab,
            Webhooks, and StoryStats.
          </Text>
        </VStack>
        <Flex gap={4} direction={{base:"column",md:"row"}} align={{base:"flex-end",md:"flex-start"}} width={{base:"100%",md:"auto"}}>
          <VStack align="end" spacing={1}>
            <Text fontSize="xl" lineHeight="1.2" fontWeight="bold" whiteSpace="nowrap">
              {billingPrice("all_access_v3", "paid+", billingInterval)}
            </Text>
            <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
              {billingInterval === "year" ? "Billed annually" : "Billed monthly"}
            </Text>
          </VStack>
          {isTrialEligible && onStartTrial ? (
            <AsyncButton
              size="md"
              colorScheme="purple"
              isDisabled={isBillingLocked}
              onClick={onStartTrial}
            >
              Try ALL-ACCESS Free
            </AsyncButton>
          ) : (
            <Button
              size="md"
              colorScheme="brand"
              isDisabled={isBillingLocked}
              onClick={onSubscribe}
            >
              Subscribe
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

// Active Subscriptions — shows paid per-sport subscriptions (including trials)
// with the appropriate cancel/reactivate controls.
//
// A trialing sub uses the same row layout as a paid sub but with:
//   - a TRIAL pill next to the tier badge
//   - a "Trial ends in Xh Xm" countdown (or "Trial ends [date]" when
//     cancel_at_period_end is scheduled) in place of the renewal date
//   - "Cancel trial" / "Upgrade now" action buttons that route to the
//     trial-specific handlers. Period-end semantics: user keeps GOAT for the
//     remaining trial window, then drops to FREE at trial_end. No Stripe
//     charge happens for canceled trials.
interface ActiveSubscriptionsProps {
  onUndoPlanChange?: (type: Type) => void;
  subscriptions: Subscription[];
  subscriptionDetails: StripeSubscriptionDetail[];
  onManage: (type: Type) => void;
  onReactivate: (type: Type) => void;
  // Trial handlers — optional to preserve backwards-compatible callers.
  onTrialCancel?: (type: Type) => void;
  onTrialReactivate?: (type: Type) => void;
  onTrialConvert?: (type: Type) => void;
  isBillingActionLocked?: (type: Type) => boolean;
  isTrialConversionPending?: (type: Type) => boolean;
}

export const ActiveSubscriptions: React.FC<ActiveSubscriptionsProps> = ({
  onUndoPlanChange,
  subscriptions,
  subscriptionDetails,
  onManage,
  onReactivate,
  onTrialCancel,
  onTrialReactivate,
  onTrialConvert,
  isBillingActionLocked,
  isTrialConversionPending,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const sectionHeaderColor = useColorModeValue("gray.700", "gray.300");
  const dividerBorderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");

  // Trialing subs render here too — they have tier=paid+ (from the trialing
  // webhook branch) so they satisfy the tier !== "free" check. ALL-ACCESS
  // still filtered out; its own banner handles it.
  const paidSubs = subscriptions.filter(
    (s) => s.tier !== "free" && !isAllAccessType(s.type),
  );

  if (paidSubs.length === 0) return null;

  return (
    <Box>
      <HStack mb={3}>
        <Heading size="sm" color={sectionHeaderColor} fontWeight="600">
          Active Subscriptions
        </Heading>
      </HStack>
      <Divider borderColor={dividerBorderColor} mb={5} />

      <Box
        bg={cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={cardBorder}
        overflow="hidden"
      >
        {paidSubs.map((sub, index) => {
          const tierInfo = TIER_INFO[sub.tier as Tier];
          const storyStatsPriceLabel = getStoryStatsPriceLabel(sub.type);
          const isStoryStatsSub = isStoryStatsType(sub.type);
          const detail = subscriptionDetails.find((d) => d.type === sub.type);
          const isPendingCancel = sub.trialConversionStatus === "cancel_scheduled" || detail?.cancelAtPeriodEnd;
          const trialing = isTrialing(sub);
          const locked = isBillingActionLocked?.(sub.type) ?? false;
          const conversionPending =
            isTrialConversionPending?.(sub.type) ?? false;
          const trialEnd = sub.trialEnd;
          const trialLabel = trialing
            ? isPendingCancel
              ? `Trial ends ${formatAbsoluteDateTime(trialEnd)}`
              : `Trial ends in ${formatTrialRemaining(trialEnd)}`
            : null;

          return (
            <React.Fragment key={sub.type}>
              {index > 0 && <Divider borderColor={dividerBorderColor} />}
              <Flex
                px={{ base: 4, md: 6 }}
                py={4}
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "start", md: "center" }}
                gap={3}
              >
                <HStack spacing={3} flex={1} minW={0} align="start">
                  <Box w="20px" h="20px" flexShrink={0} mt="2px">
                    {getSportIcon(sub.type)}
                  </Box>
                  <Text fontSize="sm" fontWeight="600" minW={0}>
                    <LeagueName type={sub.type} metadata={
                      <>
                        {trialing && trialLabel && (
                          <Text as="span" fontSize="xs" color="red.500">
                            {trialLabel}
                          </Text>
                        )}
                        {!trialing && detail?.currentPeriodEnd && (
                          <Text as="span"
                            fontSize="xs"
                            color={isPendingCancel ? "orange.500" : mutedText}
                          >
                            {isPendingCancel ? "Ends" : "Renews"}{" "}
                            {new Date(detail.currentPeriodEnd).toLocaleDateString(
                              "en-US",
                              { year: "numeric", month: "long", day: "numeric" }
                            )}
                          </Text>
                        )}
                      </>
                    } />
                  </Text>
                </HStack>

                <Flex gap={3} align="center" justify="space-between" flexWrap="wrap" width={{ base: "100%", md: "auto" }} pl={{ base: 8, md: 0 }}>
                  <VStack align={{ base: "start", md: "end" }} spacing={1} flexShrink={0}>
                    <HStack spacing={2} minH="21px">
                      <Badge
                        colorScheme={isStoryStatsSub ? "blue" : tierInfo?.colorScheme || "gray"}
                        fontSize="10px"
                      >
                        {isStoryStatsSub ? "ACTIVE" : tierInfo?.name || sub.tier}
                      </Badge>
                      {trialing && (
                        <Badge colorScheme="purple" fontSize="10px">
                          TRIAL
                        </Badge>
                      )}
                    </HStack>
                    {!trialing && (
                      <Text fontSize="xs" color={mutedText} whiteSpace="nowrap">
                        {storyStatsPriceLabel || (detail?.billingInterval === "year" ? billingPrice(sub.type, sub.tier, "year") : tierInfo?.price)}
                      </Text>
                    )}
                  </VStack>
                  <HStack spacing={3} flexWrap="wrap" rowGap={2} justify="flex-end" ml="auto">
                    {sub.trialPendingAction && sub.trialEnd ? (
                      <PendingTrialAction action={sub.trialPendingAction}
                        onCancel={() => onTrialCancel?.(sub.type)} onUndo={() => onTrialReactivate?.(sub.type)} onConvert={() => onTrialConvert?.(sub.type)} />
                    ) : conversionPending || (trialing && locked) ? (
                      <Button size="sm" variant="outline" isDisabled>
                        Processing
                      </Button>
                    ) : trialing ? (
                      isPendingCancel ? (
                        <>
                          <AsyncButton size="sm" colorScheme="brand" onClick={() => onTrialReactivate?.(sub.type)}>Undo cancellation</AsyncButton>
                  <AsyncButton size="sm" variant="outline" onClick={() => onTrialCancel?.(sub.type)}>End now</AsyncButton>

                        </>
                      ) : (
                        <>
                          {onTrialConvert && (
                            <AsyncButton
                              size="sm"
                              colorScheme="brand"
                              onClick={() => onTrialConvert(sub.type)}
                            >
                              Upgrade now
                            </AsyncButton>
                          )}
                          <AsyncButton
                            size="sm"
                            variant="outline"
                            colorScheme="orange"
                            onClick={() =>
                              onTrialCancel && onTrialCancel(sub.type)
                            }
                          >
                            Cancel trial
                          </AsyncButton>
                        </>
                      )
                    ) : isPendingCancel ? (
                      <AsyncButton
                        size="sm"
                        variant="solid"
                        colorScheme="brand"
                        isDisabled={locked}
                        onClick={() => onReactivate(sub.type)}
                      >
                        Reactivate
                      </AsyncButton>
                    ) : (
                      <Button
                        size="sm"
                        variant="solid"
                        colorScheme="gray"
                        isDisabled={locked}
                        onClick={() => onManage(sub.type)}
                      >
                        Manage
                      </Button>
                    )}
                  </HStack>
                </Flex>
              </Flex>
              {detail?.scheduledChange && (
                <Flex borderTopWidth="1px" borderColor={cardBorder} px={6} py={4} gap={3} flexWrap="wrap" align="center" justify="space-between">
                  <Text fontSize="sm">Plan changes to annual on {new Date(detail.scheduledChange.effectiveAt).toLocaleDateString("en-US", {year: "numeric", month: "long", day: "numeric"})}.</Text>
                  <AsyncButton size="sm" ml="auto" isDisabled={locked} onClick={() => onUndoPlanChange?.(sub.type)}>Undo plan change</AsyncButton>
                </Flex>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

// Subscription Table - sports grouped by category, tier-sorted within each.
// Trialing rows render a read-only TRIAL pill + countdown — the cancel /
// reactivate action buttons live in ActiveSubscriptions instead. Row CTA
// always opens the SportDetailModal (which surfaces the trial option when
// the user is eligible).
interface SubscriptionTableProps {
  excludedTypes?: Type[];
  subscriptions: Subscription[];
  subscriptionDetails?: StripeSubscriptionDetail[];
  onSportClick: (type: Type) => void;
  isMobile?: boolean;
  isBillingActionLocked?: (type: Type) => boolean;
}

export const SubscriptionTable: React.FC<SubscriptionTableProps> = ({
  excludedTypes = [],
  subscriptions,
  subscriptionDetails,
  onSportClick,
  isMobile,
  isBillingActionLocked,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const dividerColor = useColorModeValue("gray.100", "gray.700");
  const categoryBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const categoryText = useColorModeValue("gray.500", "gray.400");

  const tierLabels: Record<Tier, string> = {
    free: "FREE",
    paid: "ALL-STAR",
    "paid+": "GOAT",
  };

  const tierColors: Record<Tier, string> = {
    free: "gray",
    paid: "blue",
    "paid+": "brand",
  };

  const tierOrder: Record<Tier, number> = {
    "paid+": 0,
    paid: 1,
    free: 2,
  };

  const getSub = (type: Type): Subscription | undefined =>
    subscriptions.find((s) => s.type === type);

  const getTier = (type: Type): Tier => {
    const sub = getSub(type);
    return sub?.tier || "free";
  };

  const isRowTrialing = (type: Type): boolean => {
    const sub = getSub(type);
    return !!sub && isTrialing(sub);
  };

  const getDetail = (type: Type) =>
    subscriptionDetails?.find((d) => d.type === type);

  const formatShortDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  const getCancelDate = (type: Type): string | null => {
    const detail = getDetail(type);
    if (detail?.cancelAtPeriodEnd && detail.currentPeriodEnd) {
      return formatShortDate(detail.currentPeriodEnd);
    }
    return null;
  };

  const getButtonLabel = (type: Type): string => {
    if (isStoryStatsType(type)) {
      return getTier(type) === "free" ? "Subscribe" : "Manage";
    }
    const tier = getTier(type);
    return tier === "free" ? "Upgrade" : "Manage";
  };

  const getButtonScheme = (type: Type): string => {
    const tier = getTier(type);
    return tier === "free" ? "brand" : "gray";
  };

  const getButtonVariant = (type: Type): string => {
    const tier = getTier(type);
    return tier === "free" ? "outline" : "solid";
  };

  const categories = [
    { label: "Basketball", sports: SPORT_CATEGORIES.basketball },
    { label: "Football", sports: SPORT_CATEGORIES.football },
    { label: "Baseball", sports: SPORT_CATEGORIES.baseball },
    { label: "Soccer", sports: SPORT_CATEGORIES.soccer },
    { label: "Tennis", sports: SPORT_CATEGORIES.tennis },
    { label: "Other Sports", sports: SPORT_CATEGORIES.other },
    { label: "Esports", sports: SPORT_CATEGORIES.esports },
    { label: "StoryStats", sports: SPORT_CATEGORIES.stories },
  ].map((category) => ({
    ...category,
    sports: category.sports.filter((type) => !excludedTypes.includes(type)),
  })).filter((category) => category.sports.length > 0);

  // Sort sports within each category by tier (paid+ first, then paid, then free)
  const sortByTier = (sports: Type[]) =>
    [...sports].sort((a, b) => {
      const diff = tierOrder[getTier(a)] - tierOrder[getTier(b)];
      if (diff !== 0) return diff;
      return (PRICING[a]?.displayName || a).localeCompare(PRICING[b]?.displayName || b);
    });

  // Mobile: card list layout with category headers
  if (isMobile) {
    return (
      <VStack spacing={0} align="stretch" borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        {categories.map((category, catIndex) => {
          const sports = sortByTier(category.sports);
          return (
            <React.Fragment key={category.label}>
              <Box
                px={4}
                py={2}
                bg={categoryBg}
                borderTopWidth={catIndex > 0 ? "1px" : "0px"}
                borderColor={dividerColor}
              >
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  color={categoryText}
                >
                  {category.label}
                </Text>
              </Box>
              {sports.map((type) => {
                const tier = getTier(type);
                const cancelDate = getCancelDate(type);
                const trialing = isRowTrialing(type);
                const detail = getDetail(type);
                const locked = isBillingActionLocked?.(type) ?? false;
                const trialEnd = getSub(type)?.trialEnd ?? null;
                const trialCancelAtEnd =
                  trialing && detail?.cancelAtPeriodEnd === true;
                const trialLabel = trialing
                  ? trialCancelAtEnd
                    ? `Trial ends ${formatAbsoluteDateTime(trialEnd)}`
                    : `Trial ends in ${formatTrialRemaining(trialEnd)}`
                  : null;
                return (
                  <Flex
                    key={type}
                    px={4}
                    py={3}
                    bg={cardBg}
                    align="flex-end"
                    flexWrap="wrap"
                    gap={3}
                    justify="space-between"
                    borderTopWidth="1px"
                    borderColor={dividerColor}
                    _hover={{ bg: hoverBg }}
                    transition="background 0.15s"
                  >
                    <HStack spacing={2} flexBasis="100%" minW={0} flexWrap="wrap" rowGap={2}>
                      <Box w="20px" h="20px" flexShrink={0}>
                        {getSportIcon(type)}
                      </Box>
                      {DOC_URLS[type] ? (
                        <Link
                          href={DOC_URLS[type]}
                          isExternal
                          fontSize="sm"
                          fontWeight="600"
                          minW={0}
                          flexBasis="calc(100% - 28px)"
                          _hover={{ textDecoration: "underline" }}
                        >
                          <LeagueName type={type} />
                        </Link>
                      ) : (
                        <Text fontSize="sm" fontWeight="600">
                          <LeagueName type={type} />
                        </Text>
                      )}
                      {trialing && (
                        <Badge
                          size="sm"
                          colorScheme="purple"
                          fontSize="10px"
                        >
                          TRIAL
                        </Badge>
                      )}
                      {trialLabel && (
                        <Text fontSize="xs" color="red.500">
                          {trialLabel}
                        </Text>
                      )}
                      {!trialing && cancelDate && (
                        <Text fontSize="xs" color="orange.500">
                          until {cancelDate}
                        </Text>
                      )}
                    </HStack>
                    <HStack spacing={2} flexShrink={0} ml="auto">
                      {tier !== "free" && (
                        <Badge
                          size="sm"
                          colorScheme={tierColors[tier]}
                          fontSize="10px"
                        >
                          {isStoryStatsType(type) && tier === "paid"
                            ? "ACTIVE"
                            : tierLabels[tier]}
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant={getButtonVariant(type)}
                        colorScheme={getButtonScheme(type)}
                        isDisabled={locked}
                        onClick={() => onSportClick(type)}
                        flexShrink={0}
                      >
                        {locked ? "Processing" : getButtonLabel(type)}
                      </Button>
                    </HStack>
                  </Flex>
                );
              })}
            </React.Fragment>
          );
        })}
      </VStack>
    );
  }

  // Desktop: table layout with category header rows
  return (
    <Box
      borderWidth="1px"
      borderColor={cardBorder}
      borderRadius="lg"
      overflow="hidden"
    >
      <Table size="sm">
        <Tbody>
          {categories.map((category) => {
            const sports = sortByTier(category.sports);
            return (
              <React.Fragment key={category.label}>
                <Tr>
                  <Td
                    colSpan={2}
                    py={2}
                    px={6}
                    bg={categoryBg}
                    borderBottomWidth="0px"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wide"
                      color={categoryText}
                    >
                      {category.label}
                    </Text>
                  </Td>
                </Tr>
                {sports.map((type) => {
                  const tier = getTier(type);
                  const cancelDate = getCancelDate(type);
                  const trialing = isRowTrialing(type);
                  const detail = getDetail(type);
                  const locked = isBillingActionLocked?.(type) ?? false;
                  const trialEnd = getSub(type)?.trialEnd ?? null;
                  const trialCancelAtEnd =
                    trialing && detail?.cancelAtPeriodEnd === true;
                  const trialLabel = trialing
                    ? trialCancelAtEnd
                      ? `Trial ends ${formatAbsoluteDateTime(trialEnd)}`
                      : `Trial ends in ${formatTrialRemaining(trialEnd)}`
                    : null;
                  return (
                    <Tr
                      key={type}
                      _hover={{ bg: hoverBg }}
                      transition="background 0.15s"
                    >
                      <Td py={3}>
                        <HStack spacing={3} flexWrap="wrap" rowGap={2}>
                          <Box w="20px" h="20px" flexShrink={0}>
                            {getSportIcon(type)}
                          </Box>
                          {DOC_URLS[type] ? (
                            <Link
                              href={DOC_URLS[type]}
                              isExternal
                              fontSize="sm"
                              fontWeight="500"
                              minW={0}
                              flex={1}
                              _hover={{ textDecoration: "underline" }}
                            >
                              <LeagueName type={type} />
                            </Link>
                          ) : (
                            <Text fontSize="sm" fontWeight="500">
                              <LeagueName type={type} />
                            </Text>
                          )}
                          {trialing && (
                            <Badge
                              size="sm"
                              colorScheme="purple"
                              fontSize="10px"
                            >
                              TRIAL
                            </Badge>
                          )}
                          {trialLabel && (
                            <Text fontSize="xs" color="red.500">
                              {trialLabel}
                            </Text>
                          )}
                          {!trialing && cancelDate && (
                            <Text fontSize="xs" color="orange.500">
                              until {cancelDate}
                            </Text>
                          )}
                        </HStack>
                      </Td>
                      <Td py={3} textAlign="right">
                        <HStack spacing={3} justify="flex-end">
                          {tier !== "free" && (
                            <Badge
                              size="sm"
                              colorScheme={tierColors[tier]}
                              fontSize="10px"
                            >
                              {isStoryStatsType(type) && tier === "paid"
                                ? "ACTIVE"
                                : tierLabels[tier]}
                            </Badge>
                          )}
                          <Button
                            size="sm"
                            variant={getButtonVariant(type)}
                            colorScheme={getButtonScheme(type)}
                            isDisabled={locked}
                            onClick={() => onSportClick(type)}
                          >
                            {locked ? "Processing" : getButtonLabel(type)}
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

// Sport Detail Modal - shows endpoints and subscribe options.
//
// Trial-aware: three display states driven by `isCurrentlyTrialing` +
// `isTrialEligible`.
//   1) Currently trialing: trial status banner at top, tier cards rendered
//      locked (non-clickable, dimmed) for layout parity, footer action is
//      "Cancel trial" while renewing; canceled trials offer only "Upgrade now".
//      Matches the row-level Cancel/Reactivate on ActiveSubscriptions so the
//      user can manage the trial from either surface.
//   2) Eligible + not trialing: purple trial callout banner with "Start trial"
//      primary button, tier cards below remain clickable so the user can
//      direct-subscribe without a trial (e.g. via the GOAT card).
//   3) Otherwise: existing behavior — tier cards drive Subscribe / Upgrade /
//      Downgrade / Cancel / Reactivate via onTierSelect + onReactivate.
// Interrupted requests use the same action and confirmation as the original request.
const PendingTrialAction = ({ action, onCancel, onUndo, onConvert }: {
  action: NonNullable<Subscription["trialPendingAction"]>;
  onCancel: () => void;
  onUndo: () => void;
  onConvert: () => void;
}) => (
  <AsyncButton size="sm" colorScheme="brand"
    onClick={action === "convert" ? onConvert : action === "undo" ? onUndo : onCancel}>
    {action === "convert" ? "Upgrade now" : action === "undo" ? "Undo cancellation" : action === "immediate" ? "End now" : "Cancel trial"}
  </AsyncButton>
);

interface SportDetailModalProps {
  billingInterval?: BillingInterval;
  currentBillingInterval?: BillingInterval;
  onBillingIntervalChange?: (value: BillingInterval) => void;
  isOpen: boolean;
  onClose: () => void;
  sport: Type | null;
  currentTier: Tier;
  isPendingCancel?: boolean;
  cancelEndDate?: string | null;
  onTierSelect: (tier: Tier) => void;
  onReactivate?: () => void;
  // Trial integration. Optional so existing callers continue to compile.
  isTrialEligible?: boolean;
  onStartTrial?: (type: Type) => void;
  isCurrentlyTrialing?: boolean;
  trialEnd?: string | null;
  trialPendingAction?: Subscription["trialPendingAction"];
  onTrialCancel?: (type: Type) => void;
  onTrialReactivate?: (type: Type) => void;
  onTrialConvert?: (type: Type) => void;
  isBillingLocked?: boolean;
  isTrialConversionPending?: boolean;
}

export const SportDetailModal: React.FC<SportDetailModalProps> = ({
  isOpen,
  onClose,
  sport,
  currentTier,
  billingInterval: requestedBillingInterval = "month", currentBillingInterval = "month", onBillingIntervalChange,
  isPendingCancel,
  cancelEndDate,
  onTierSelect,
  onReactivate,
  isTrialEligible,
  onStartTrial,
  isCurrentlyTrialing,
  trialEnd,
  trialPendingAction,
  onTrialCancel,
  onTrialReactivate,
  onTrialConvert,
  isBillingLocked,
  isTrialConversionPending,
}) => {
  const billingInterval = currentTier !== "free" && currentBillingInterval === "year" ? "year" : requestedBillingInterval;
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const docLinkBg = useColorModeValue("gray.50", "gray.700");
  const docLinkHoverBg = useColorModeValue("gray.100", "gray.600");

  // Tracks the async reactivate-subscription click on a tier card so we can
  // show a spinner in place and lock the card grid until it resolves.
  const [isReactivating, setIsReactivating] = React.useState(false);

  if (!sport) return null;

  const pricing = PRICING[sport];
  const docUrl = DOC_URLS[sport];
  const accountTiersUrl = docUrl ? `${docUrl}/#account-tiers` : null;
  const storyStatsInfo = getStoryStatsProductInfo(sport);
  const storyStatsPriceLabel = getStoryStatsPriceLabel(sport);

  const getTierColor = (tier: Tier) => {
    const colors: Record<Tier, string> = {
      free: "gray",
      paid: "blue",
      "paid+": "brand",
    };
    return colors[tier];
  };

  if (storyStatsInfo) {
    const isActive = currentTier === "paid";
    const actionLabel = isPendingCancel
      ? "Reactivate"
      : isActive
        ? "Cancel"
        : "Subscribe";

    const handleStoryStatsClick = async () => {
      if (isPendingCancel && onReactivate) {
        if (isReactivating) return;
        setIsReactivating(true);
        try {
          await onReactivate();
        } finally {
          setIsReactivating(false);
        }
        return;
      }
      onTierSelect(isActive ? "free" : "paid");
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg={bgColor} borderRadius="xl">
          <ModalHeader pb={2}>
            <HStack spacing={3}>
              {getSportIcon(sport)}
              <VStack align="start" spacing={0}>
                <Heading size="md">{storyStatsInfo.displayName}</Heading>
                <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                  <Text fontSize="sm" color="gray.500">
                    Current:
                  </Text>
                  <Badge colorScheme={isActive ? "blue" : "gray"}>
                    {isActive ? "ACTIVE" : "FREE"}
                  </Badge>
                </HStack>
                {isPendingCancel && cancelEndDate && (
                  <Text fontSize="xs" color="orange.500">
                    Ends{" "}
                    {new Date(cancelEndDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </Text>
                )}
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={5} align="stretch">
              <Box
                borderWidth={isActive ? "2px" : "1px"}
                borderColor={isActive ? "blue.500" : borderColor}
                borderRadius="lg"
                p={5}
                bg={isActive ? "blue.50" : "transparent"}
                _dark={{ bg: isActive ? "blue.900" : "transparent" }}
              >
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between" align="start">
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="blue.500">
                        StoryStats package
                      </Text>
                      <Text mt={1} fontSize="lg" fontWeight="bold">
                        {storyStatsPriceLabel}
                      </Text>
                    </Box>
                    {isActive && (
                      <Badge colorScheme="blue" variant="subtle">
                        Active
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
                    {storyStatsInfo.description}
                  </Text>
                  <VStack align="start" spacing={1} fontSize="xs" color="gray.500">
                    <Text>Pregame, live, and postgame stories</Text>
                    <Text>Betting and stats audiences</Text>
                    <Text>Editorial and humor tones</Text>
                    <Text>120 requests per minute</Text>
                  </VStack>
                  <AsyncButton
                    alignSelf="start"
                    colorScheme={isActive && !isPendingCancel ? "orange" : "brand"}
                    variant={isActive && !isPendingCancel ? "outline" : "solid"}
                    isDisabled={isBillingLocked || isReactivating}
                    onClick={handleStoryStatsClick}
                  >
                    {isBillingLocked
                      ? "Processing"
                      : isReactivating
                        ? "Reactivating"
                        : actionLabel}
                  </AsyncButton>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor} borderRadius="xl">
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            {getSportIcon(sport)}
            <VStack align="start" spacing={0}>
              <Heading size="md">{pricing.displayName}</Heading>
              <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                <Text fontSize="sm" color="gray.500">
                  Current:
                </Text>
                <Badge colorScheme={getTierColor(currentTier)}>
                  {TIER_INFO[currentTier].name}
                </Badge>
                {isCurrentlyTrialing && (
                  <Badge colorScheme="purple" fontSize="10px">
                    TRIAL
                  </Badge>
                )}
              </HStack>
              {isCurrentlyTrialing && trialEnd && (
                <Text
                  fontSize="xs"
                  color="red.500"
                  fontWeight="500"
                >
                  {isPendingCancel
                    ? `Trial ends ${formatAbsoluteDateTime(trialEnd)}`
                    : `Trial ends in ${formatTrialRemaining(trialEnd)}`}
                </Text>
              )}
              {!isCurrentlyTrialing && isPendingCancel && cancelEndDate && (
                <Text fontSize="xs" color="orange.500">
                  Ends{" "}
                  {new Date(cancelEndDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              )}
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={5} align="stretch">
            {/* Trial status banner — shown when user is currently on a trial */}
            {(isCurrentlyTrialing || isTrialConversionPending) && sport && (
              <Box
                borderWidth="1px"
                borderColor="purple.200"
                bg="purple.50"
                borderRadius="lg"
                p={4}
                _dark={{ bg: "purple.900", borderColor: "purple.700" }}
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  align={{ base: "start", md: "center" }}
                  justify="space-between"
                  gap={3}
                >
                  <VStack align="start" spacing={1}>
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color="gray.900"
                      _dark={{ color: "white" }}
                    >
                      {isTrialConversionPending
                        ? "GOAT upgrade processing"
                        : "GOAT trial active"}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="gray.700"
                      _dark={{ color: "gray.200" }}
                    >
                      {isTrialConversionPending
                        ? "Your upgrade is being processed. Billing actions are locked until it settles."
                        : isPendingCancel
                        ? `Cancellation scheduled. You'll keep GOAT access until ${formatAbsoluteDateTime(trialEnd ?? null)}. No charge for the GOAT upgrade.`
                        : "No charge until the trial ends. Cancel anytime below."}
                    </Text>
                  </VStack>
                  {trialPendingAction && trialEnd ? (
                    <PendingTrialAction action={trialPendingAction}
                      onCancel={() => onTrialCancel?.(sport)} onUndo={() => onTrialReactivate?.(sport)} onConvert={() => onTrialConvert?.(sport)} />
                  ) : isTrialConversionPending ||
                  (isCurrentlyTrialing && isBillingLocked) ? (
                    <Button size="sm" variant="outline" isDisabled>
                      Processing
                    </Button>
                  ) : isPendingCancel ? (
                    <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                      <AsyncButton size="sm" colorScheme="brand" onClick={() => onTrialReactivate?.(sport)}>Undo cancellation</AsyncButton>
                      <AsyncButton size="sm" variant="outline" onClick={() => onTrialCancel?.(sport)}>End now</AsyncButton>
                    </HStack>
                  ) : (
                    <HStack spacing={2} flexWrap="wrap" justify="flex-end">
                      {onTrialConvert && (
                        <AsyncButton
                          size="sm"
                          colorScheme="brand"
                          onClick={() => onTrialConvert(sport)}
                        >
                          Upgrade now
                        </AsyncButton>
                      )}
                      <AsyncButton
                        size="sm"
                        variant="outline"
                        colorScheme="orange"
                        onClick={() => onTrialCancel && onTrialCancel(sport)}
                      >
                        Cancel trial
                      </AsyncButton>
                    </HStack>
                  )}
                </Flex>
              </Box>
            )}

            {/* Trial CTA — shown above the tier grid when eligible (not already trialing) */}
            {!isCurrentlyTrialing && isTrialEligible && onStartTrial && (
              <Box
                borderWidth="1px"
                borderColor="purple.200"
                bg="purple.50"
                borderRadius="lg"
                p={4}
                _dark={{ bg: "purple.900", borderColor: "purple.700" }}
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  align={{ base: "start", md: "center" }}
                  justify="space-between"
                  gap={3}
                >
                  <VStack align="start" spacing={1}>
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color="gray.900"
                      _dark={{ color: "white" }}
                    >
                      Try GOAT free for 7 days
                    </Text>
                    <Text
                      fontSize="xs"
                      color="gray.700"
                      _dark={{ color: "gray.200" }}
                    >
                      Full endpoint access. Card required. No charge until the
                      trial ends — cancel anytime.
                    </Text>
                  </VStack>
                  <AsyncButton
                    size="sm"
                    colorScheme="purple"
                    onClick={() => onStartTrial(sport)}
                  >
                    Start trial
                  </AsyncButton>
                </Flex>
              </Box>
            )}

            {onBillingIntervalChange && !(currentTier !== "free" && currentBillingInterval === "year") && <BillingIntervalToggle value={billingInterval} onChange={onBillingIntervalChange} disabled={isCurrentlyTrialing || isBillingLocked} />}
            {/* Tier Selection */}
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }} gap={3}>
              {(["free", "paid", "paid+"] as Tier[]).map((tier) => {
                const info = TIER_INFO[tier];
                const isActive = currentTier === tier && (tier === "free" || currentBillingInterval === billingInterval);

                // Determine the action for this tier
                const getAction = () => {
                  if (tier === "free") {
                    if (isPendingCancel) return "cancel_pending";
                    return currentTier !== "free" ? "cancel" : "current";
                  }
                  if (isActive && isCurrentlyTrialing) return "current";
                  if (isActive && isPendingCancel) return "reactivate";
                  if (isActive) return "cancel";
                  if (currentTier === tier && currentBillingInterval !== billingInterval) return "change_period";
                  if (currentTier === "paid+" && tier === "paid") return "downgrade";
                  if (currentTier === "paid" && tier === "paid+") return "upgrade";
                  return "subscribe";
                };
                const action = getAction();

                const isReactivatingThisCard =
                  isReactivating && action === "reactivate";

                // Handle click for the whole card
                const handleCardClick = async () => {
                  if (action === "current") return;
                  if (action === "reactivate" && onReactivate) {
                    if (isReactivating) return;
                    setIsReactivating(true);
                    try {
                      await onReactivate();
                    } finally {
                      setIsReactivating(false);
                    }
                    return;
                  }
                  if (action === "cancel") {
                    onTierSelect("free");
                  } else {
                    onTierSelect(tier);
                  }
                };

                // Tier cards lock while the user is in a trial — the backend
                // 409-guards tier changes during `trialing` status, so the UI
                // mirrors that. Trial lifecycle goes through the banner above.
                // Cards also lock while a reactivate request is in flight so
                // the user can't stack duplicate clicks.
                const isClickable =
                  !isCurrentlyTrialing &&
                  !isBillingLocked &&
                  !isReactivating &&
                  action !== "current" &&
                  action !== "cancel_pending";

                return (
                  <Box
                    key={tier}
                    as={isClickable ? "button" : "div"}
                    onClick={isClickable ? handleCardClick : undefined}
                    borderWidth={isActive ? "2px" : "1px"}
                    borderColor={isActive ? `${info.colorScheme}.500` : borderColor}
                    borderRadius="lg"
                    p={4}
                    bg={isActive ? `${info.colorScheme}.50` : "transparent"}
                    cursor={isClickable ? "pointer" : "default"}
                    transition="all 0.2s"
                    opacity={
                      isCurrentlyTrialing
                        ? 0.6
                        : isReactivating && !isReactivatingThisCard
                          ? 0.5
                          : 1
                    }
                    aria-busy={isReactivatingThisCard || undefined}
                    _hover={
                      isClickable
                        ? {
                            borderColor: `${info.colorScheme}.400`,
                            transform: "translateY(-2px)",
                            boxShadow: "md",
                          }
                        : {}
                    }
                    _dark={{
                      bg: isActive ? `${info.colorScheme}.900` : "transparent",
                    }}
                  >
                    <VStack spacing={2}>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={
                          info.colorScheme === "brand"
                            ? "accent"
                            : `${info.colorScheme}.500`
                        }
                      >
                        {info.name}
                      </Text>
                      <Text fontSize="lg" fontWeight="bold">
                        {tier === "free" ? info.price : billingPrice(sport, tier, billingInterval)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {info.description}
                      </Text>
                      {action === "change_period" && <Badge colorScheme="blue">Switch to {billingInterval === "year" ? "annual" : "monthly"}</Badge>}
                      {action === "current" && (
                        <Badge colorScheme="gray" variant="subtle">
                          Current Plan
                        </Badge>
                      )}
                      {action === "reactivate" &&
                        (isReactivatingThisCard ? (
                          <Spinner
                            size="sm"
                            color="green.500"
                            thickness="2px"
                            speed="0.7s"
                            aria-label="Reactivating"
                          />
                        ) : (
                          <Badge
                            colorScheme="green"
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="600"
                          >
                            Reactivate
                          </Badge>
                        ))}
                      {action === "cancel" && (
                        <Badge
                          colorScheme={tier === "free" ? "orange" : "red"}
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="600"
                        >
                          {tier === "free" ? "Downgrade" : "Cancel"}
                        </Badge>
                      )}
                      {action === "cancel_pending" && (
                        <Badge
                          colorScheme="gray"
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="600"
                        >
                          Scheduled
                        </Badge>
                      )}
                      {action === "downgrade" && (
                        <Badge
                          colorScheme="orange"
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="600"
                        >
                          Downgrade
                        </Badge>
                      )}
                      {action === "upgrade" && (
                        <Badge
                          colorScheme="brand"
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="600"
                        >
                          Upgrade
                        </Badge>
                      )}
                      {action === "subscribe" && (
                        <Badge
                          colorScheme="brand"
                          variant="subtle"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="xs"
                          fontWeight="600"
                        >
                          Subscribe
                        </Badge>
                      )}
                    </VStack>
                  </Box>
                );
              })}
            </Grid>

            {/* Documentation Link */}
            {accountTiersUrl && (
              <Link
                href={accountTiersUrl}
                isExternal
                _hover={{ textDecoration: "none" }}
              >
                <Flex
                  bg={docLinkBg}
                  borderRadius="lg"
                  p={4}
                  align="center"
                  justify="space-between"
                  transition="all 0.2s"
                  _hover={{ bg: docLinkHoverBg }}
                >
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="600">
                      View available endpoints by tier
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      See the full list of endpoints included in each plan
                    </Text>
                  </VStack>
                  <ExternalLinkIcon color="gray.400" boxSize={4} />
                </Flex>
              </Link>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const LeagueName = ({ type, metadata }: { type: Type; metadata?: React.ReactNode }) => {
  const pricing = PRICING[type];
  return (
    <Box as="span" display="block" overflowWrap="anywhere" whiteSpace="normal">
      {pricing?.fullName || pricing?.displayName || type}
      {(pricing?.abbreviation || metadata) && (
        <Flex as="span" mt={1} fontSize="xs" fontWeight="normal" color="gray.500" gap={2} flexWrap="wrap" align="baseline">
          {pricing?.abbreviation && <Text as="span">{pricing.abbreviation}</Text>}
          {metadata}
        </Flex>
      )}
    </Box>
  );
};
